'use client';

import { useEffect, useRef } from 'react';
import { trackUnitView } from '@repo/api';
import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';
import { loadListingAlert } from '@/app/saved/hooks/listingAlertStore';

/** The eight values the BE can return, mirroring the analytics param. */
type InferredParam =
  | 'none'
  | 'location'
  | 'bedroom'
  | 'rent'
  | 'location_bedroom'
  | 'location_rent'
  | 'bedroom_rent'
  | 'location_bedroom_rent';

export function UnitViewTracker({ unitId }: { unitId: string }) {
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const analytics = useAnalytics();
  const lastTrackedUnitIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!unitId) return;
    if (!isLoggedIn) return;
    if (lastTrackedUnitIdRef.current === unitId) return;
    lastTrackedUnitIdRef.current = unitId;

    /*
     * The response carries the outcome of the server's listing-alert
     * inference, which runs on the visit that takes this renter to their 2nd
     * distinct unit. It is null on every other view.
     *
     * The event is fired from here rather than from the server so that
     * `instant_alerts_on_auto` stays on ONE pipeline: the search-filter path
     * already reports it through this client, and the alerts funnel is
     * measured on that. A server-side publish would land in the SNS stream
     * instead and split the funnel in two.
     */
    void trackUnitView(unitId)
      .then(async result => {
        const inference = result?.listingAlertInference;
        if (!inference?.alertsEnabled) return;

        void analytics.track('instant_alerts_on_auto', {
          source: 'unit',
          inferred: inference.inferred as InferredParam,
        });

        // Refetch so a mounted /saved card or profile hub shows alerts as on
        // straight away instead of holding the row from before the view.
        await loadListingAlert(true).catch(() => null);
      })
      .catch(() => {
        // Tracking is best-effort and must never surface on a unit page.
      });
  }, [analytics, isLoggedIn, unitId]);

  return null;
}
